/**
 * Einbuchen (G-11): Planzeilen-Auswahl des Tages + Status + Hinweis.
 * 409 (Abwesenheits-Guard, ausser RUECKRUF) → Fehler-Toast mit Backend-Meldung.
 */

import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { api, apiError } from "@/services/api"
import type { Anwesenheitsstatus, ShiftPlanEntry } from "@/types"
import { ATTENDANCE_STATUS, ATTENDANCE_STATUS_VALUES } from "./labels"
import { PlanEntryPicker } from "./PlanEntryPicker"

interface CheckInSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Stichtag des Boards ("YYYY-MM-DD") */
  date: string
}

export function CheckInSheet({ open, onOpenChange, date }: CheckInSheetProps) {
  const queryClient = useQueryClient()
  const [entry, setEntry] = useState<ShiftPlanEntry | null>(null)
  const [status, setStatus] = useState<Anwesenheitsstatus>("ANWESEND")
  const [info, setInfo] = useState("")

  useEffect(() => {
    if (open) {
      setEntry(null)
      setStatus("ANWESEND")
      setInfo("")
    }
  }, [open])

  const checkIn = useMutation({
    mutationFn: () =>
      api.attendance.checkIn({
        shiftPlanEntryId: Number(entry!.id),
        status,
        info: info.trim() || null,
      }),
    onSuccess: (record) => {
      queryClient.invalidateQueries({ queryKey: ["attendance"] })
      queryClient.invalidateQueries({ queryKey: ["shiftPlan"] })
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      toast.success(
        `${record.employeeName} eingebucht (${ATTENDANCE_STATUS[record.status].label})`
      )
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="attendance-check-in" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Einbuchen</SheetTitle>
          <SheetDescription>
            Status ≠ Anwesend bucht eine Pauschale (8 h) und meldet den Grund (G-11).
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          <PlanEntryPicker date={date} value={entry} onChange={setEntry} excludeCheckedIn />

          <div className="space-y-1.5">
            <Label>Status *</Label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as Anwesenheitsstatus)}
            >
              {ATTENDANCE_STATUS_VALUES.map((s) => (
                <option key={s} value={s}>
                  {ATTENDANCE_STATUS[s].label}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Hinweis (optional)</Label>
            <Textarea
              rows={2}
              value={info}
              onChange={(e) => setInfo(e.target.value)}
              placeholder="z. B. kommt später, Arzttermin …"
            />
          </div>
        </div>
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button disabled={!entry || checkIn.isPending} onClick={() => checkIn.mutate()}>
            Einbuchen
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
